import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useMemo, useRef, useState } from "react";
import ImgUpload from "../../../Images/dummy_image.webp";
import CloseIcon from "@mui/icons-material/Close";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { useFormik } from "formik";
import { useCreateCompanyMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { appColor } from "../../../theme/appColor";
import { useStyles } from "../../../theme/appStyles";
import { uploadImage } from "../../../Components/UploadFile/upload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [imageSrc, setImageSrc] = useState<string>(ImgUpload);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [newCompany, { isLoading }] = useCreateCompanyMutation();
  const { showErrorMessage, showMessage } = useNotifier();

  const [selectedImage, setSelectedImage] = React.useState<File | undefined>(
    undefined
  );
  const [isImageUploading, setIsImageUploading] = React.useState(false);

  const formik = useFormik({
    initialValues: {
      companyName: "",
      displayName: "",
      companyImg: "",
      address: "",
      description: "",
      openingHours: daysOfWeek.map(() => ({
        hour: "",
        minute: "",
      })),
      closingHours: daysOfWeek.map(() => ({
        hour: "",
        minute: "",
      })),
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const formattedOpeningHours = values.openingHours.map(
          ({ hour, minute }) => `${hour}:${minute}`
        );
        const formattedClosingHours = values.closingHours.map(
          ({ hour, minute }) => `${hour}:${minute}`
        );

        const formData = new FormData();
        let companyImg: string | undefined = "";

        if (selectedImage) {
          setIsImageUploading(true);
          formData.append("companyImg", selectedImage);
          companyImg = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }

        const temData = {
          companyName: values.companyName,
          displayName: values.displayName,
          address: values.address,
          description: values.description,
          companyImg,
          openingHours: formattedOpeningHours,
          closingHours: formattedClosingHours,
        };

        const addCompanyResponse = await newCompany(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage("Company Created successfully");
          resetForm();
          setSelectedImage(undefined);
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return formik.values.companyName === "" ||
      formik.values.companyName === undefined ||
      formik.values.displayName === "" ||
      formik.values.displayName === undefined
      ? false
      : true;
  }, [formik]);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          setImageSrc(reader.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
  
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
  
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
  
          let width = img.width;
          let height = img.height;
  
          // Calculate the new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }
  
          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);
  
          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile);
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };
  
        img.onerror = (error) => reject(error);
      };
    });
  };
  
  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
  
    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const maxSize = 2048 * 1024;
  
      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
        setSelectedImage(undefined);
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 2 MB.");
        setSelectedImage(undefined);
      } else {
        try {
          const resizedImage = await resizeImage(file, 500, 500);
          setSelectedImage(resizedImage);
        } catch (error) {
          showErrorMessage("Failed to resize the image.");
          setSelectedImage(undefined);
        }
      }
    } else {
      setSelectedImage(undefined);
    }
  };

  const handleRemoveImage = () => {
    setImageSrc(ImgUpload);
  };

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={3} md={3} sm={12} xs={12}>
          <Card className={classes.stepperCard}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ borderBottom: 2, padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                pt={2}
                alignContent={"center"}
              >
                <Typography className={classes.cardTitle}>
                  Create Company
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item lg={9} md={9} sm={12} xs={12}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Add Company Image
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} p={"24px"}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Box className={classes.imgBox}>
                        {selectedImage && (
                          <img
                            src={URL.createObjectURL(selectedImage)}
                            alt="Uploaded"
                            className={classes.SelectImageBox}
                          />
                        )}
                        <div>
                          <input
                            accept="image/*"
                            style={{ display: "none" }}
                            id="icon-button-file-1"
                            type="file"
                            onChange={handleImageChange}
                          />
                          <div
                            style={{
                              textAlign: "center",
                            }}
                          >
                            <label htmlFor="icon-button-file-1">
                              <IconButton
                                color="primary"
                                aria-label="upload picture"
                                component="span"
                              >
                                <CloudUploadIcon sx={{ fontSize: 45 }} />
                              </IconButton>
                            </label>
                            <div>PNG, JPG and JPEG are allowed</div>
                          </div>
                        </div>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Company Information
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Company Name
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Enter Company Name"
                            size="small"
                            {...formik.getFieldProps("companyName")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Display Name
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Enter Display Name"
                            size="small"
                            {...formik.getFieldProps("displayName")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">Address</Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Enter Company Address"
                            size="small"
                            {...formik.getFieldProps("address")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Description
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Enter Description .."
                            size="small"
                            multiline
                            rows={8}
                            {...formik.getFieldProps("description")}
                            className={classes.discriptionTextField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Opening & Closing Hours
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} p={"24px"}>
                    {daysOfWeek.map((day, index) => (
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center" spacing={5}>
                          <Grid item lg={2} md={2} sm={12} xs={12}>
                            <Typography variant="subtitle1">{day}</Typography>
                          </Grid>
                          <Grid item lg={2.5} md={2} sm={6} xs={6}>
                            <TextField
                              select
                              size="small"
                              {...formik.getFieldProps(
                                `openingHours[${index}].hour`
                              )}
                              className={classes.textField}
                              SelectProps={{
                                native: true,
                              }}
                              defaultValue=""
                              InputLabelProps={{ shrink: true }}
                            >
                              <option value="" disabled>
                                Opening Hours
                              </option>
                              {Array.from(Array(24).keys()).map((hour) => (
                                <option
                                  key={hour}
                                  value={hour < 10 ? `0${hour}` : `${hour}`}
                                >
                                  {hour < 10 ? `0${hour}` : `${hour}`}
                                </option>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item lg={2.5} md={2} sm={6} xs={6}>
                            <TextField
                              select
                              size="small"
                              {...formik.getFieldProps(
                                `openingHours[${index}].minute`
                              )}
                              className={classes.textField}
                              SelectProps={{
                                native: true,
                              }}
                              defaultValue=""
                              InputLabelProps={{ shrink: true }}
                            >
                              <option value="" disabled>
                                Minutes
                              </option>
                              {Array.from(Array(60).keys()).map((minute) => (
                                <option
                                  key={minute}
                                  value={
                                    minute < 10 ? `0${minute}` : `${minute}`
                                  }
                                >
                                  {minute < 10 ? `0${minute}` : `${minute}`}
                                </option>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item lg={2.5} md={2} sm={6} xs={6}>
                            <TextField
                              select
                              size="small"
                              {...formik.getFieldProps(
                                `closingHours[${index}].hour`
                              )}
                              className={classes.textField}
                              SelectProps={{
                                native: true,
                              }}
                              defaultValue=""
                              InputLabelProps={{ shrink: true }}
                            >
                              <option value="" disabled>
                                Closing Hours
                              </option>
                              {Array.from(Array(24).keys()).map((hour) => (
                                <option
                                  key={hour}
                                  value={hour < 10 ? `0${hour}` : `${hour}`}
                                >
                                  {hour < 10 ? `0${hour}` : `${hour}`}
                                </option>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item lg={2.5} md={2} sm={6} xs={6}>
                            <TextField
                              select
                              size="small"
                              {...formik.getFieldProps(
                                `closingHours[${index}].minute`
                              )}
                              className={classes.textField}
                              SelectProps={{
                                native: true,
                              }}
                              defaultValue=""
                              InputLabelProps={{ shrink: true }}
                            >
                              <option value="" disabled>
                                Minutes
                              </option>
                              {Array.from(Array(60).keys()).map((minute) => (
                                <option
                                  key={minute}
                                  value={
                                    minute < 10 ? `0${minute}` : `${minute}`
                                  }
                                >
                                  {minute < 10 ? `0${minute}` : `${minute}`}
                                </option>
                              ))}
                            </TextField>
                          </Grid>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                <Card className={classes.card}>
                  <Grid container spacing={3} p={"24px"}>
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        className={classes.addButton}
                        onClick={() => formik.resetForm()}
                      >
                        Cancel
                      </Button>
                      <Box m={0.5}></Box>

                      <Button
                        variant="contained"
                        startIcon={<SaveAltIcon />}
                        className={classes.addButton}
                        onClick={() => formik.handleSubmit()}
                        disabled={!formValid || isLoading}
                      >
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
